import { UrlSegment } from "@angular/router";
import {
    AppDefinitionManagerService,
    CategoryData
} from "@app/services/state/app-definition-manager.service";
import {
    CategoryPath,
    DataPath,
    SectionPath,
    VariablePath
} from "@app/services/state/app-navigation-manager.service";
import { SubjectInstance } from "@app/services/state/app-subject-manager.service";
import { FormConfigurationDatasetSettings, SubjectVariableData } from "@fhir-api";

export function withoutSection<T extends DataPath | VariablePath>(path: T): T {
    return {
        ...path,
        category: undefined,
        section: undefined
    };
}

export function withoutCategory<T extends DataPath | VariablePath>(path: T): T {
    return {
        ...path,
        category: undefined
    };
}

export function createPath<
    T extends string | undefined,
    U extends string | undefined,
    V extends string | undefined,
    W extends string | undefined
>(
    datasetUri: string,
    subjectUri: string,
    section?: T,
    category?: U,
    variable?: V
): T extends string
    ? U extends string
        ? V extends string
            ? CategoryPath & VariablePath
            : CategoryPath
        : V extends string
          ? SectionPath & VariablePath
          : SectionPath
    : V extends string
      ? VariablePath
      : DataPath {
    return {
        datasetUri,
        subjectUri,
        section,
        category,
        variable
    } as T extends string
        ? U extends string
            ? V extends string
                ? CategoryPath & VariablePath
                : CategoryPath
            : V extends string
              ? SectionPath & VariablePath
              : SectionPath
        : V extends string
          ? VariablePath
          : DataPath;
}

export function createInitPath(datasetName: string, parentPath?: DataPath): CategoryPath {
    return createPath(
        parentPath ? parentPath.datasetUri + "." + datasetName : datasetName,
        parentPath ? parentPath.subjectUri + ".initializationSubject" : "initializationSubject",
        "init",
        "init-1"
    );
}

export function getListOfChildPaths(path: DataPath): DataPath[] {
    const paths: DataPath[] = [path];
    let parent = getParentPath(path, 1);
    while (parent.datasetUri.length > 0) {
        paths.push(parent);
        parent = getParentPath(parent, 1);
    }
    return paths.reverse();
}

/**
 * Returns the loaded subjects from the root subject through the subject in the current path.
 * An incomplete subject chain is omitted until all ancestors are available.
 */
export function getSubjectBreadcrumbs(
    path: DataPath | null | undefined,
    subjects: Record<string, SubjectInstance> | null | undefined
): SubjectInstance[] {
    if (!path || !subjects) {
        return [];
    }

    const breadcrumbs: SubjectInstance[] = [];
    for (const { subjectUri } of getListOfChildPaths(path)) {
        const subject = subjects[subjectUri];
        if (!subject) {
            return [];
        }
        breadcrumbs.push(subject);
    }
    return breadcrumbs;
}

export function extendWithCategoryData(
    path: VariablePath,
    categoryData: CategoryData | null
): VariablePath {
    if (!categoryData) {
        return path;
    }
    return {
        ...path,
        category: categoryData.category,
        section: categoryData.section
    };
}

export function isRootPath(path: DataPath): boolean {
    return path.datasetUri.split(".").length === 1;
}

export function rootDatasetFromApi(datasetUri: string): string {
    return datasetUri.split(".")[0];
}

export function rootDatasetFromPath(path: DataPath): string {
    return rootDatasetFromApi(path.datasetUri);
}

export function rootSubjectFromApi(subjectUri: string): string {
    return subjectUri.split(".")[0];
}

export function rootSubjectFromPath(path: DataPath): string {
    return rootSubjectFromApi(path.subjectUri);
}

export function variableListToObject(
    variables: SubjectVariableData[]
): Record<string, string | undefined> {
    return variables.reduce(
        (acc, variable) => {
            acc[variable.name] = variable.value;
            return acc;
        },
        {} as Record<string, string | undefined>
    );
}

export function getParentPath(path: DataPath, level: number = 1): DataPath {
    const subjectUri = path.subjectUri.split(".");
    const datasetUri = path.datasetUri.split(".");
    if (subjectUri.length < level || datasetUri.length < level) {
        throw new Error("Path level is too high");
    }
    return {
        subjectUri: level > 0 ? subjectUri.slice(0, -level).join(".") : subjectUri.join("."),
        datasetUri: level > 0 ? datasetUri.slice(0, -level).join(".") : datasetUri.join("."),
        section: undefined,
        category: undefined,
        variable: undefined
    };
}

export function subjectToPath(subject: SubjectInstance): DataPath {
    return {
        subjectUri: subject.subjectUri,
        datasetUri: subject.datasetUri
    };
}

export function pathToSubjectUrl(path: DataPath): string {
    let navigationUrl = "";
    const datasets = path.datasetUri.split(".");
    const subject = path.subjectUri.split(".");
    for (let i = 0; i < subject.length; i++) {
        navigationUrl += "/" + (i == 0 ? "subject" : datasets[i]) + "/" + subject[i];
    }
    return navigationUrl;
}

export function urlToPath(rootDatasetName: string, url: UrlSegment[]): DataPath {
    let subjects: string[] = [];
    let datasets: string[] = [rootDatasetName];
    url.forEach((segment, index) => {
        if (index % 2 === 0) {
            subjects.push(segment.path);
        } else {
            datasets.push(segment.path);
        }
    });
    return {
        subjectUri: subjects.join("."),
        datasetUri: datasets.join(".")
    };
}

export function apiPathToName(apiPath: string): string {
    const parts = apiPath.split(".");
    if (parts.length === 0) {
        throw new Error("Invalid API path");
    }
    return parts[parts.length - 1];
}

export function pathDatasetName(path: DataPath): string {
    return apiPathToName(path.datasetUri);
}

export function pathSubjectName(path: DataPath): string {
    return apiPathToName(path.subjectUri);
}

export function goToChild(
    path: DataPath,
    datasetName: string,
    subjectName: string,
    section?: string,
    category?: string,
    variable?: string
): DataPath {
    return {
        ...path,
        datasetUri: path.datasetUri.length > 0 ? path.datasetUri + "." + datasetName : datasetName,
        subjectUri: subjectName.startsWith(path.subjectUri) ? subjectName : path.subjectUri + "." + subjectName,
        section,
        category,
        variable
    };
}

export function sortSubjectInstances(
    subjects: SubjectInstance[],
    sorts: FormConfigurationDatasetSettings["subjectSort"]
): SubjectInstance[] {
    if (!sorts?.length) {
        return subjects;
    }

    return [...subjects].sort((left, right) => {
        for (const sort of sorts) {
            const leftValue = left.variable?.[sort.sortBy];
            const rightValue = right.variable?.[sort.sortBy];
            const leftIsEmpty = leftValue === undefined || leftValue === "";
            const rightIsEmpty = rightValue === undefined || rightValue === "";

            if (leftIsEmpty || rightIsEmpty) {
                if (leftIsEmpty !== rightIsEmpty) {
                    return leftIsEmpty ? 1 : -1;
                }
                continue;
            }

            let comparison: number;
            switch (sort.sortType) {
                case "number":
                    comparison = Number(leftValue) - Number(rightValue);
                    break;
                case "date":
                    comparison = Date.parse(leftValue) - Date.parse(rightValue);
                    break;
                case "string":
                    comparison = leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0;
                    break;
            }

            if (comparison !== 0) {
                const direction = sort.sortOrder === "ascending" ? 1 : -1;
                return direction * comparison;
            }
        }

        return 0;
    });
}

export function addSection(path: DataPath, section: string): SectionPath {
    return {
        ...path,
        section
    };
}

export function addCategory(path: SectionPath, category: string): CategoryPath {
    return {
        ...path,
        category
    };
}

export function addVariable(path: DataPath, variable: string): VariablePath {
    return {
        ...path,
        variable
    };
}
